import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import { faSliders } from '@fortawesome/pro-solid-svg-icons';

import { AnyObject, PlatformApp, PlatformAppInstance, Schema } from '@typings';
import { APP_INSTANCE_REF_LITERAL } from '@constants';
import { getPlatformAppInstanceIntegrationValues } from '@services';
import { contextNamesSelector, platformAppsSelector } from '@selectors';
import { useSelector } from '@hooks';
import {
  as,
  cn,
  formatToCamelCase,
  getParamsSchema,
  invariant,
  isIntegrationType,
  isObject,
  isObjectEmpty,
  isPlatformAppsCustomComponent,
  isString,
} from '@utils';
import { isSchemaAnyOf, isSchemaContainer, isSchemaField } from '@utils/guards';

import { SchemaContext } from '@components/Providers';
import { Button, Field, Icon, Modal, ModalRef } from '@components/Shared';
import { FieldSideAction } from '@components/Ui/FieldSideAction';

import { SchemaAppIntegrationsModal } from './SchemaAppIntegrationsModal';
import { SchemaAppLogo } from './SchemaAppLogo';

type Integration = {
  instance_id: string;
  type: typeof APP_INSTANCE_REF_LITERAL;
  path: string;
};

type Props = {
  className?: string;
  schema: Schema.Model;
  children?: React.ReactNode;
};
export const SchemaAppIntegrations: React.FC<Props> = ({
  className,
  schema,
  schema: { typeName, metaType, name },
  children,
}) => {
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);
  const { templates, instances } = useSelector(platformAppsSelector);

  const {
    getValues,
    setValue: setFormValue,
    getFieldState,
    clearErrors,
  } = useFormContext();

  const { importedConfig } = useContext(SchemaContext);

  const modalRef = useRef<ModalRef>(null);

  const isAppIntegration =
    isIntegrationType(metaType) && !isPlatformAppsCustomComponent(typeName);

  const [isManual, setManual] = useState(isAppIntegration && !getValues(name));
  const [value, setValue] =
    useState<PlatformAppInstance.IntegrationValue | null>(null);
  /**
   * Value of form state before integration was applied
   */
  const [prevValue, setPrevValue] = useState<AnyObject | null>(null);

  const extractError = (error: AnyObject): string | undefined => {
    if (!isObject(error)) {
      return;
    }

    return Object.values(error).reduce((acc, error) => {
      if (isString(acc) && !!acc) {
        return acc;
      }

      if (isObject(error) && (error as FieldError).message) {
        return (error as FieldError).message as string;
      }

      if (
        isObject(error) &&
        !(error as AnyObject).message &&
        !(error as AnyObject).type
      ) {
        return extractError(error as FieldError);
      }

      return acc;
    }, '');
  };

  const error: string | undefined = (() => {
    if (!isAppIntegration || isManual) {
      return;
    }

    const { error } = getFieldState(name);

    if (!isObject(error)) {
      return;
    }

    if (isString(error.root?.message)) {
      return error.root?.message;
    }

    return extractError(error);
  })();

  const isRequired = useMemo(() => {
    if (!isAppIntegration) {
      return false;
    }

    const isNodeRequired = (schema: Schema.Model) => {
      if (isSchemaField(schema)) {
        return schema.properties.required;
      }

      if (isSchemaAnyOf(schema)) {
        return schema.children.some(isNodeRequired);
      }

      if (isSchemaContainer(schema)) {
        return schema.children.some(isNodeRequired);
      }

      return false;
    };

    return isNodeRequired(schema);
  }, [isAppIntegration, schema]);

  const getInstanceValues = useCallback(
    async ({ instance_id: instanceId, path }: Integration) => {
      try {
        invariant(organizationName);
        invariant(clusterName);
        invariant(projectName);

        const { items: values } = await getPlatformAppInstanceIntegrationValues(
          {
            organizationName,
            clusterName,
            projectName,
            instanceId,
          },
        );

        return values.find(
          (value) => value.path === path && value.instanceId === instanceId,
        );
      } catch (error) {
        /**
         * Continue regardless error
         */
      }
    },
    [organizationName, clusterName, projectName],
  );

  /**
   * Render initial fields to create form state
   */
  useEffect(() => {
    if (!isAppIntegration || prevValue) {
      return;
    }

    Promise.resolve().then(() => {
      const formValue = getValues(name);

      if (!formValue) {
        return;
      }

      setManual(false);
      setPrevValue(formValue);
    });
  }, [isAppIntegration, name, prevValue, getValues]);

  useEffect(() => {
    const handleIntegration = async (integration: Integration) => {
      try {
        const { instance_id: instanceId } = integration;

        const instanceValue = await getInstanceValues(integration);

        if (!instanceValue) {
          return;
        }

        const instance = instances.find(({ id }) => id === instanceId);

        if (!instance) {
          return;
        }

        const { displayName, templateName, templateVersion } = instance;
        const template = templates.find(({ name }) => templateName === name);

        if (!template) {
          return;
        }

        const {
          title,
          shortDescription: description,
          logo,
        } = formatToCamelCase(template) as PlatformApp.Template;
        const value: PlatformAppInstance.IntegrationValue = {
          ...instanceValue,
          title: displayName || title,
          templateName,
          templateVersion,
          description,
          logo,
          params: getParamsSchema(template.output),
        };

        setManual(false);
        setValue(value);
      } catch (error) {
        /**
         * Continue regardless error
         */
      }
    };

    if (name in importedConfig) {
      const value = importedConfig[name];

      if (!isObject(value)) {
        return;
      }

      if (value.type === APP_INSTANCE_REF_LITERAL) {
        handleIntegration(value as Integration);
      } else {
        setManual(true);
        setPrevValue(value);
      }
    }
    /**
     * We don't want to track `instance` since it is re-fetched intervally
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, importedConfig, getInstanceValues]);

  useEffect(() => {
    const handleIntegration = async (integration: Integration) => {
      try {
        const { instance_id: instanceId } = integration;

        const instanceValue = await getInstanceValues(integration);

        if (!instanceValue) {
          return;
        }

        const instance = instances.find(({ id }) => id === instanceId);

        if (!instance) {
          return;
        }

        const { displayName, templateName, templateVersion } = instance;
        const template = templates.find(({ name }) => templateName === name);

        if (!template) {
          return;
        }

        const {
          title,
          shortDescription: description,
          logo,
        } = formatToCamelCase(template) as PlatformApp.Template;
        const value: PlatformAppInstance.IntegrationValue = {
          ...instanceValue,
          title: displayName || title,
          templateName,
          templateVersion,
          description,
          logo,
          params: getParamsSchema(template.output),
        };

        setManual(false);
        setValue(value);
      } catch (error) {
        /**
         * Continue regardless error
         */
      }
    };

    if (name in importedConfig) {
      const value = importedConfig[name];

      if (!isObject(value)) {
        return;
      }

      if (value.type === APP_INSTANCE_REF_LITERAL) {
        handleIntegration(value as Integration);
      } else {
        setManual(true);
        setPrevValue(value);
      }
    }
    /**
     * We don't want to track `instance` since it is re-fetched intervally
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, importedConfig, getInstanceValues]);

  const handleIntegrationApply = (
    value: PlatformAppInstance.IntegrationValue | null,
  ) => {
    const isManual = value === null;

    setValue(value);
    setManual(isManual);
    clearErrors(name);

    if (!isManual) {
      const { appInstanceId, path } = as.o(value);

      setFormValue(name, {
        type: APP_INSTANCE_REF_LITERAL,
        instance_id: appInstanceId,
        path,
      });
    } else {
      setFormValue(name, prevValue);
    }
  };

  const handleIntegrationReset = () => {
    /**
     * Set inital hidden form state to make sure form payload is correct
     */
    if (
      (isObject(value) || isManual) &&
      isObject(prevValue) &&
      !isObjectEmpty(prevValue)
    ) {
      setFormValue(name, prevValue);
    }

    setValue(null);
    setManual(false);
  };

  const label = (
    <p
      className={cn('text-neural-04', {
        'text-neural-04 text-caption': !!value || isManual,
        'required-label': isRequired,
      })}
    >
      {typeName}
    </p>
  );

  const renderContent = () => {
    if (isManual) {
      return (
        <div className="flex w-full items-center gap-2">
          <Icon
            icon={faSliders}
            className="h-10 w-10 rounded-md bg-rebecca text-[23px] text-white"
          />
          <p className="capitalize">Manual mode</p>
        </div>
      );
    }

    if (!value) {
      return (
        <>
          <SchemaAppLogo className="h-10 w-10" />
          {label}
        </>
      );
    }

    const { title, logo, path } = value;

    return (
      <div className="flex w-full items-center gap-2">
        <SchemaAppLogo src={logo} className="h-10 w-10" />
        <div className="flex flex-col">
          <p>{title}</p>
          <p className="ml-1 mt-0.5 text-caption text-neural-04">{path}</p>
        </div>
      </div>
    );
  };

  const renderSideButton = () => {
    if (isManual) {
      return (
        <Button
          variant="white"
          className="h-10 w-max rounded-lg"
          onClick={handleIntegrationReset}
        >
          Cancel
        </Button>
      );
    }

    if (!value) {
      return (
        <Button
          className="h-10 w-max rounded-lg capitalize"
          onClick={modalRef.current?.open}
        >
          Choose app
        </Button>
      );
    }

    return (
      <Button
        variant="white-error"
        className="h-10 w-max rounded-lg"
        onClick={handleIntegrationReset}
      >
        Stop
      </Button>
    );
  };

  if (!isAppIntegration || !typeName) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return (
    <>
      <Modal
        ref={modalRef}
        content={
          <SchemaAppIntegrationsModal
            typeName={typeName}
            manualMode={isManual}
            value={value}
            onApply={handleIntegrationApply}
          />
        }
      >
        <Button theme className="mt-4 w-full text-left">
          <Field.Label
            variant="static"
            className="text-body-small text-neural-04"
          >
            App Integration
          </Field.Label>
          <div className="group flex">
            <div
              className={cn(
                'peer flex w-full items-center gap-2 rounded-l-lg border border-transparent bg-neural-01 p-2 transition-colors hover:bg-primary-subtle',
                className,
                { 'border-error': !!error },
              )}
            >
              {renderContent()}
            </div>
            <FieldSideAction>{renderSideButton()}</FieldSideAction>
          </div>
        </Button>
      </Modal>
      <Field.Error>{error}</Field.Error>
      {isManual && children}
    </>
  );
};
